import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Loader2, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../lib/api';
import FileInput from '../components/ui/FileInput';

interface AppSettings {
  nepal_payment: {
    qrCodeUrl?: string;
    bankName?: string;
    accountName?: string;
    proMediumAmount?: number;
    proLiveAmount?: number;
    instructions: string[];
    isEnabled: boolean;
  };
  maintenance: {
    isEnabled: boolean;
    message?: string;
  };
}

const defaultSettings: AppSettings = {
  nepal_payment: {
    bankName: '',
    accountName: '',
    proMediumAmount: 0,
    proLiveAmount: 0,
    instructions: [],
    isEnabled: false,
  },
  maintenance: { isEnabled: false, message: '' },
};

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<AppSettings>(defaultSettings);

  const { data, isLoading } = useQuery<AppSettings>({
    queryKey: ['settings'],
    queryFn: async () => {
      const res = await api.get('/settings');
      return res.data.data;
    },
  });

  useEffect(() => {
    if (data) {
      setForm({
        nepal_payment: { ...defaultSettings.nepal_payment, ...data.nepal_payment },
        maintenance: { ...defaultSettings.maintenance, ...data.maintenance },
      });
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: async (settings: AppSettings) => {
      await api.put('/settings', settings);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Settings saved');
    },
    onError: () => toast.error('Failed to save settings'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(form);
  };

  const addInstruction = () => {
    setForm({
      ...form,
      nepal_payment: {
        ...form.nepal_payment,
        instructions: [...form.nepal_payment.instructions, ''],
      },
    });
  };

  const updateInstruction = (idx: number, value: string) => {
    const updated = [...form.nepal_payment.instructions];
    updated[idx] = value;
    setForm({ ...form, nepal_payment: { ...form.nepal_payment, instructions: updated } });
  };

  const removeInstruction = (idx: number) => {
    setForm({
      ...form,
      nepal_payment: {
        ...form.nepal_payment,
        instructions: form.nepal_payment.instructions.filter((_, i) => i !== idx),
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 mt-1">Global application configuration</p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
        <div className="card space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Maintenance</h2>

          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-gray-700">Maintenance Mode</label>
              <p className="text-xs text-gray-400">Disable all user-facing features</p>
            </div>
            <button
              type="button"
              onClick={() => setForm({ ...form, maintenance: { ...form.maintenance, isEnabled: !form.maintenance.isEnabled } })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.maintenance.isEnabled ? 'bg-danger-500' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.maintenance.isEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {form.maintenance.isEnabled && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Maintenance Message</label>
              <input
                type="text"
                value={form.maintenance.message || ''}
                onChange={(e) => setForm({ ...form, maintenance: { ...form.maintenance, message: e.target.value } })}
                className="input-field"
                placeholder="Service is under maintenance..."
              />
            </div>
          )}
        </div>

        <div className="card space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Nepal Payment</h2>
            <button
              type="button"
              onClick={() => setForm({ ...form, nepal_payment: { ...form.nepal_payment, isEnabled: !form.nepal_payment.isEnabled } })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.nepal_payment.isEnabled ? 'bg-success-500' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.nepal_payment.isEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
              <input
                type="text"
                value={form.nepal_payment.bankName || ''}
                onChange={(e) => setForm({ ...form, nepal_payment: { ...form.nepal_payment, bankName: e.target.value } })}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Name</label>
              <input
                type="text"
                value={form.nepal_payment.accountName || ''}
                onChange={(e) => setForm({ ...form, nepal_payment: { ...form.nepal_payment, accountName: e.target.value } })}
                className="input-field"
              />
            </div>
          </div>

          <FileInput
            label="QR Code Image"
            value={form.nepal_payment.qrCodeUrl || ''}
            onChange={(url) => setForm({ ...form, nepal_payment: { ...form.nepal_payment, qrCodeUrl: url } })}
            accept="image"
            placeholder="Select QR code image"
          />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pro Medium Price (NPR)</label>
              <input
                type="number"
                value={form.nepal_payment.proMediumAmount || 0}
                onChange={(e) => setForm({ ...form, nepal_payment: { ...form.nepal_payment, proMediumAmount: Number(e.target.value) } })}
                className="input-field"
                min={0}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pro Live Price (NPR)</label>
              <input
                type="number"
                value={form.nepal_payment.proLiveAmount || 0}
                onChange={(e) => setForm({ ...form, nepal_payment: { ...form.nepal_payment, proLiveAmount: Number(e.target.value) } })}
                className="input-field"
                min={0}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Payment Instructions</label>
              <button type="button" onClick={addInstruction} className="text-primary-600 hover:text-primary-700 text-xs font-medium flex items-center gap-1">
                <Plus size={14} /> Add
              </button>
            </div>
            <div className="space-y-2">
              {form.nepal_payment.instructions.map((instruction, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={instruction}
                    onChange={(e) => updateInstruction(idx, e.target.value)}
                    className="input-field"
                    placeholder={`Step ${idx + 1}`}
                  />
                  <button type="button" onClick={() => removeInstruction(idx)} className="text-gray-400 hover:text-danger-500">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              {form.nepal_payment.instructions.length === 0 && (
                <p className="text-sm text-gray-400">No instructions added.</p>
              )}
            </div>
          </div>
        </div>

        <button type="submit" disabled={mutation.isPending} className="btn-primary flex items-center gap-2">
          {mutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {mutation.isPending ? 'Saving...' : 'Save Settings'}
        </button>
      </form>
    </div>
  );
}
